import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { onCallService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';

const OnCallsContext = createContext(null);

export const useOnCalls = () => {
  const context = useContext(OnCallsContext);
  if (!context) {
    throw new Error('useOnCalls must be used within an OnCallsProvider');
  }
  return context;
};

export const OnCallsProvider = ({ children }) => {
  const [onCalls, setOnCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();
  const { selectedTeam } = useTeam();
  const [initialized, setInitialized] = useState(false);
  
  // Flag pour éviter les appels multiples
  const isLoadingRef = useRef(false);

  // Charger toutes les astreintes
  const loadOnCalls = useCallback(async (forceReload = false, teamOverride = null) => {
    // Si déjà en cours de chargement, ne pas relancer
    if (isLoadingRef.current && !forceReload) {
      console.log('⏳ OnCallsContext - Chargement déjà en cours, abandon');
      return;
    }

    // Si pas d'utilisateur connecté, nettoyer
    if (!currentUser?.id) {
      console.log('👤 OnCallsContext - Utilisateur non connecté:', {
        userId: currentUser?.id,
        userEmail: currentUser?.email
      });
      setOnCalls([]);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      isLoadingRef.current = true;
      setLoading(true);
      setError(null);
      
      const teamToUse = teamOverride || selectedTeam;
      console.log('📅 OnCallsContext - Chargement des astreintes pour:', currentUser?.email, 'équipe:', teamToUse?.name);

      const data = await onCallService.getAll(teamToUse?.id);
      setOnCalls(data);
      setInitialized(true);
    } catch (err) {
      console.error('❌ OnCallsContext - Erreur:', err);
      setError(err.message);
      setOnCalls([]);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [currentUser?.id, currentUser?.email, selectedTeam?.id]);

  // Nettoyer les données quand l'utilisateur se déconnecte
  useEffect(() => {
    if (!currentUser?.id && initialized) {
      console.log('🧹 OnCallsContext - Nettoyage des données utilisateur');
      setOnCalls([]);
      setError(null);
      setInitialized(false);
      setLoading(false);
    }
  }, [currentUser?.id, initialized]);

  // Écouter les changements d'équipe
  useEffect(() => {
    const handleTeamChange = (event) => {
      const { team, userId } = event.detail;
      
      if (userId === currentUser?.id) {
        console.log('🔄 OnCallsContext - Changement d\'équipe détecté:', team.name);
        loadOnCalls(true, team); // Force le rechargement avec la nouvelle équipe
      }
    };

    window.addEventListener('teamChanged', handleTeamChange);
    
    return () => {
      window.removeEventListener('teamChanged', handleTeamChange);
    };
  }, [currentUser?.id, loadOnCalls]);

  // Ajouter une astreinte
  const addOnCall = useCallback(async (onCallData) => {
    try {
      setError(null);
      
      // Ajouter le teamId à partir de l'équipe sélectionnée
      const onCallDataWithTeam = {
        ...onCallData,
        teamId: selectedTeam?.id
      };
      
      console.log('➕ Ajout astreinte:', onCallDataWithTeam);
      
      const newOnCall = await onCallService.create(onCallDataWithTeam);
      setOnCalls(prev => [...prev, newOnCall].sort((a, b) => new Date(a.startDate) - new Date(b.startDate)));
      console.log('✅ Astreinte ajoutée:', newOnCall);
      
      return newOnCall;
    } catch (err) {
      console.error('❌ Erreur lors de l\'ajout de l\'astreinte:', err);
      setError(err.message || 'Erreur lors de l\'ajout de l\'astreinte');
      throw err;
    }
  }, [selectedTeam?.id]);

  // Mettre à jour une astreinte
  const updateOnCall = useCallback(async (id, onCallData) => {
    try {
      setError(null);
      
      // Ajouter le teamId à partir de l'équipe sélectionnée
      const onCallDataWithTeam = {
        ...onCallData,
        teamId: selectedTeam?.id
      };
      
      console.log('✏️ Mise à jour astreinte:', id, onCallDataWithTeam);
      
      const updatedOnCall = await onCallService.update(id, onCallDataWithTeam);
      setOnCalls(prev => 
        prev.map(onCall => onCall.id === id ? updatedOnCall : onCall)
            .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
      );
      console.log('✅ Astreinte mise à jour:', updatedOnCall);
      
      return updatedOnCall;
    } catch (err) {
      console.error('❌ Erreur lors de la mise à jour de l\'astreinte:', err);
      setError(err.message || 'Erreur lors de la mise à jour de l\'astreinte');
      throw err;
    }
  }, [selectedTeam?.id]);

  // Supprimer une astreinte
  const deleteOnCall = useCallback(async (id) => {
    try {
      setError(null);
      console.log('🗑️ Suppression astreinte:', id);
      
      await onCallService.delete(id);
      setOnCalls(prev => prev.filter(onCall => onCall.id !== id));
      console.log('✅ Astreinte supprimée:', id);
    } catch (err) {
      console.error('❌ Erreur lors de la suppression de l\'astreinte:', err);
      setError(err.message || 'Erreur lors de la suppression de l\'astreinte');
      throw err;
    }
  }, [selectedTeam]);

  // Récupérer une astreinte par ID
  const getOnCallById = useCallback((id) => {
    return onCalls.find(onCall => onCall.id === parseInt(id));
  }, [onCalls]);

  /**
   * Synchronisation intelligente depuis import Liisa/Visual Planning.
   * @param {Array} importedOnCalls - astreintes parsées {technicianId, startDate, endDate, ...}
   * @param {Function} onProgress - callback({percent, label})
   */
  const syncLiisaOnCalls = useCallback(async (importedOnCalls, onProgress, filterStart = null, filterEnd = null) => {
    onProgress?.({ percent: 10, label: 'Analyse des astreintes existantes…' });

    // Plage de synchronisation : filtre utilisateur si défini, sinon étendue du fichier
    let rangeStart, rangeEnd;
    if (filterStart || filterEnd) {
      rangeStart = filterStart
        ? filterStart.toISOString().split('T')[0]
        : importedOnCalls.reduce((min, oc) => oc.startDate < min ? oc.startDate : min, importedOnCalls[0]?.startDate || '');
      rangeEnd = filterEnd
        ? filterEnd.toISOString().split('T')[0]
        : importedOnCalls.reduce((max, oc) => oc.endDate > max ? oc.endDate : max, importedOnCalls[0]?.endDate || '');
    } else {
      const dates = importedOnCalls.map(oc => new Date(oc.startDate));
      rangeStart = new Date(Math.min(...dates)).toISOString().split('T')[0];
      rangeEnd   = new Date(Math.max(...importedOnCalls.map(oc => new Date(oc.endDate)))).toISOString().split('T')[0];
    }

    // Astreintes existantes dans cette plage (même équipe, gérées côté serveur via teamId)
    const existingInRange = onCalls.filter(oc => {
      const s = new Date(oc.startDate);
      return s >= new Date(rangeStart) && s <= new Date(rangeEnd);
    });

    onProgress?.({ percent: 30, label: 'Calcul des différences…' });

    // Clé de correspondance : technicianId + startDate (yyyy-MM-dd)
    const importedKeys = new Set(
      importedOnCalls.map(oc => `${oc.technicianId}|${oc.startDate}`)
    );
    const existingKeys = new Map(
      existingInRange.map(oc => [`${oc.technicianId}|${oc.startDate.split('T')[0]}`, oc])
    );

    const toCreate = importedOnCalls.filter(
      oc => !existingKeys.has(`${oc.technicianId}|${oc.startDate}`)
    );
    const toDelete = existingInRange
      .filter(oc => !importedKeys.has(`${oc.technicianId}|${oc.startDate.split('T')[0]}`))
      .map(oc => oc.id);

    const unchanged = existingInRange.length - toDelete.length;

    onProgress?.({ percent: 50, label: `Envoi : ${toCreate.length} création(s), ${toDelete.length} suppression(s)…` });

    const result = await onCallService.bulkSync({ toCreate, toDelete, rangeStart, rangeEnd });

    onProgress?.({ percent: 90, label: 'Rechargement…' });
    await loadOnCalls(true);
    onProgress?.({ percent: 100, label: 'Terminé' });

    return { created: result.created, deleted: result.deleted, unchanged };
  }, [onCalls, loadOnCalls]);

  // Fonction d'initialisation manuelle
  const initializeData = useCallback(async (userOverride = null) => {
    const userToUse = userOverride || currentUser;
    
    if (!userToUse?.id) {
      console.log('⚠️ OnCallsContext - Initialisation sans utilisateur connecté');
      return;
    }
    
    console.log('🔄 OnCallsContext - Initialisation manuelle pour', userToUse.email);
    setInitialized(false);
    setLoading(true);
    setError(null);
    
    try {
      await loadOnCalls(true);
      setInitialized(true);
      console.log('✅ OnCallsContext - Initialisation terminée');
    } catch (error) {
      console.error('❌ OnCallsContext - Erreur lors de l\'initialisation:', error);
      setError(error.message || 'Erreur lors de l\'initialisation');
      setOnCalls([]);
    } finally {
      setLoading(false);
    }
  }, [loadOnCalls, currentUser]);

  const value = {
    // État
    onCalls,
    loading,
    error,
    initialized,
    
    // Actions
    loadOnCalls,
    addOnCall,
    updateOnCall,
    deleteOnCall,
    
    // Utilitaires
    getOnCallById,
    syncLiisaOnCalls,
    
    // Stats
    totalOnCalls: onCalls.length,
    
    // Initialisation
    initializeData,
  };

  return (
    <OnCallsContext.Provider value={value}>
      {children}
    </OnCallsContext.Provider>
  );
}; 